export const isPlainObject = (val: unknown): val is Record<string, unknown> =>
  typeof val === 'object' && val !== null && !Array.isArray(val);

const decodeRefSegment = (seg: string): string =>
  seg.replace(/~1/g, '/').replace(/~0/g, '~');

const resolveRef = (
  root: Record<string, unknown>,
  ref: string
): unknown | undefined => {
  if (!ref.startsWith('#/')) return undefined;
  const path = ref
    .slice(2)
    .split('/')
    .map((s) => decodeRefSegment(s));

  let node: unknown = root;
  for (const seg of path) {
    if (isPlainObject(node) && seg in node) {
      node = (node as Record<string, unknown>)[seg];
    } else {
      return undefined;
    }
  }
  return node;
};

export const derefSchema = (
  node: unknown,
  root: Record<string, unknown>,
  visited: WeakSet<object>
): unknown => {
  if (!isPlainObject(node)) return node;

  const obj = node as unknown as object;
  if (visited.has(obj)) return node;
  visited.add(obj);

  const ref = (node as { $ref?: unknown }).$ref;
  if (typeof ref === 'string') {
    const resolved = resolveRef(root, ref);
    if (resolved && resolved !== node) {
      return derefSchema(resolved, root, visited);
    }
  }

  return node;
};

export const derefSchemaDeep = (
  node: unknown,
  root: Record<string, unknown>,
  memo = new WeakMap<object, unknown>()
): unknown => {
  if (Array.isArray(node)) {
    return node.map((v) => derefSchemaDeep(v, root, memo));
  }
  if (!isPlainObject(node)) return node;

  const obj = node as unknown as object;
  const cached = memo.get(obj);
  if (cached !== undefined) return cached;

  const ref = (node as { $ref?: unknown }).$ref;
  if (typeof ref === 'string') {
    const resolved = resolveRef(root, ref);
    if (resolved !== undefined) {
      return derefSchemaDeep(resolved, root, memo);
    }
  }

  const isNotNothingSchema = (value: unknown): boolean => {
    if (!isPlainObject(value)) return false;
    const obj = value as Record<string, unknown>;
    if (!('not' in obj)) return false;
    if (Object.keys(obj).length !== 1) return false;
    return isPlainObject(obj.not) && Object.keys(obj.not).length === 0;
  };

  const simplifyUnionKey = (target: Record<string, unknown>, key: string) => {
    const raw = target[key];
    if (!Array.isArray(raw)) return;
    const filtered = raw.filter((entry) => !isNotNothingSchema(entry));
    if (filtered.length !== raw.length) {
      target[key] = filtered;
    }

    const current = target[key];
    if (
      Array.isArray(current) &&
      current.length === 1 &&
      Object.keys(target).every((k) => k === key)
    ) {
      const only = current[0];
      if (isPlainObject(only)) {
        Object.keys(target).forEach((k) => delete target[k]);
        Object.assign(target, only);
      } else {
        memo.set(obj, only);
      }
    }
  };

  const out: Record<string, unknown> = {};
  memo.set(obj, out);
  Object.entries(node).forEach(([k, v]) => {
    if (k === '$ref') return;
    out[k] = derefSchemaDeep(v, root, memo);
  });

  simplifyUnionKey(out, 'anyOf');
  simplifyUnionKey(out, 'oneOf');
  simplifyUnionKey(out, 'allOf');

  return memo.get(obj) ?? out;
};

export const getSchemaProps = (schema: unknown): Record<string, unknown> => {
  if (!isPlainObject(schema)) return {};
  const root = schema as Record<string, unknown>;
  const visited = new WeakSet<object>();

  const props: Record<string, unknown> = {};

  const visit = (node: unknown) => {
    const resolved = derefSchema(node, root, visited);
    if (!isPlainObject(resolved)) return;

    if (
      (resolved as { type?: unknown }).type === 'object' &&
      isPlainObject((resolved as { properties?: unknown }).properties)
    ) {
      const rawProps = (resolved as { properties: Record<string, unknown> })
        .properties;
      for (const [key, value] of Object.entries(rawProps)) {
        props[key] = derefSchema(value, root, visited);
      }
    }

    for (const key of ['allOf', 'anyOf', 'oneOf'] as const) {
      const parts = (resolved as Record<string, unknown>)[key];
      if (Array.isArray(parts)) {
        parts.forEach(visit);
      }
    }
  };

  visit(schema);
  return props;
};

export const getSchemaRequired = (schema: unknown): Set<string> => {
  const required = new Set<string>();
  if (!isPlainObject(schema)) return required;
  const root = schema as Record<string, unknown>;
  const visited = new WeakSet<object>();

  const visit = (node: unknown) => {
    const resolved = derefSchema(node, root, visited);
    if (!isPlainObject(resolved)) return;

    const req = (resolved as { required?: unknown }).required;
    if (Array.isArray(req)) {
      req.forEach((k) => {
        if (typeof k === 'string') required.add(k);
      });
    }
    for (const key of ['allOf', 'anyOf', 'oneOf'] as const) {
      const parts = (resolved as Record<string, unknown>)[key];
      if (Array.isArray(parts)) {
        parts.forEach(visit);
      }
    }
  };

  visit(schema);
  return required;
};

export type ActionInputType =
  | 'string'
  | 'number'
  | 'integer'
  | 'boolean'
  | 'object'
  | 'array'
  | 'unknown';

export const getSchemaEnumValues = (
  schema: unknown,
  visited = new WeakSet<object>()
): Array<string | number> | undefined => {
  if (!isPlainObject(schema)) return undefined;

  const obj = schema as unknown as object;
  if (visited.has(obj)) return undefined;
  visited.add(obj);

  const out: Array<string | number> = [];
  const seen = new Set<string | number>();

  const add = (v: unknown) => {
    if (typeof v !== 'string' && typeof v !== 'number') return;
    if (seen.has(v)) return;
    seen.add(v);
    out.push(v);
  };

  const enumRaw = (schema as { enum?: unknown }).enum;
  if (Array.isArray(enumRaw)) {
    enumRaw.forEach(add);
  }

  const constRaw = (schema as { const?: unknown }).const;
  add(constRaw);

  for (const key of ['anyOf', 'oneOf', 'allOf'] as const) {
    const parts = (schema as Record<string, unknown>)[key];
    if (!Array.isArray(parts)) continue;
    parts.forEach((part) => {
      const nested = getSchemaEnumValues(part, visited);
      nested?.forEach(add);
    });
  }

  return out.length > 0 ? out : undefined;
};

export const normalizeSchemaType = (schema: unknown): ActionInputType => {
  if (!isPlainObject(schema)) return 'unknown';
  const t = (schema as { type?: unknown }).type;
  const valid = new Set([
    'string',
    'number',
    'integer',
    'boolean',
    'object',
    'array',
  ]);
  if (typeof t === 'string' && valid.has(t)) {
    return t as ActionInputType;
  }
  if (Array.isArray(t)) {
    const first = t.find((x) => typeof x === 'string' && valid.has(x));
    if (typeof first === 'string') return first as ActionInputType;
  }

  for (const key of ['anyOf', 'oneOf', 'allOf'] as const) {
    const parts = (schema as Record<string, unknown>)[key];
    if (!Array.isArray(parts) || parts.length === 0) continue;
    const types = parts
      .map((p) => normalizeSchemaType(p))
      .filter((x) => x !== 'unknown');
    if (types.length === 0) continue;
    if (types.every((x) => x === types[0])) {
      return types[0];
    }

    const typeSet = new Set(types);
    const primitive = new Set(['string', 'number', 'integer', 'boolean']);
    const onlyPrimitives = Array.from(typeSet).every((x) => primitive.has(x));
    if (onlyPrimitives) {
      if (typeSet.has('string')) return 'string';
      if (typeSet.has('number') || typeSet.has('integer')) return 'number';
      if (typeSet.has('boolean')) return 'boolean';
    }
  }
  return 'unknown';
};

export const safeJsonStringify = (value: unknown): string => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value ?? '');
  }
};

export type SchemaVariant = {
  id: string;
  label: string;
  schema: Record<string, unknown>;
  properties: Record<string, unknown>;
  required: Set<string>;
  constFields: Record<string, string | number | boolean>;
};

export const getConstLikeValue = (
  schema: unknown
): string | number | boolean | undefined => {
  if (!isPlainObject(schema)) return undefined;
  const constRaw = (schema as { const?: unknown }).const;
  if (
    typeof constRaw === 'string' ||
    typeof constRaw === 'number' ||
    typeof constRaw === 'boolean'
  ) {
    return constRaw;
  }
  const enumRaw = (schema as { enum?: unknown }).enum;
  if (Array.isArray(enumRaw) && enumRaw.length === 1) {
    const only = enumRaw[0];
    if (
      typeof only === 'string' ||
      typeof only === 'number' ||
      typeof only === 'boolean'
    ) {
      return only;
    }
  }
  return undefined;
};

export const getTopLevelObjectVariants = (schema: unknown): SchemaVariant[] => {
  if (!isPlainObject(schema)) return [];
  const root = schema as Record<string, unknown>;
  const hasUnion = ['anyOf', 'oneOf'].some(
    (key) =>
      Array.isArray((root as Record<string, unknown>)[key]) &&
      (root as Record<string, unknown>)[key]
  );
  if (!hasUnion) return [];

  const visited = new WeakSet<object>();
  const rootRecord = root as Record<string, unknown>;

  const unions: Array<{
    schema: Record<string, unknown>;
    discriminator?: { key: string; constValue?: string | number | boolean };
  }> = [];

  (['anyOf', 'oneOf'] as const).forEach((key) => {
    const parts = rootRecord[key];
    if (!Array.isArray(parts)) return;
    parts.forEach((entry) => {
      if (!isPlainObject(entry)) return;
      const record = derefSchema(entry, rootRecord, visited);
      if (!isPlainObject(record)) return;

      let discriminator:
        | { key: string; constValue?: string | number | boolean }
        | undefined;
      const props = (record as { properties?: unknown }).properties;
      if (isPlainObject(props)) {
        Object.entries(props).forEach(([k, v]) => {
          const constVal = getConstLikeValue(v);
          if (constVal !== undefined) {
            discriminator = { key: k, constValue: constVal };
          }
        });
      }

      unions.push({
        schema: record,
        discriminator,
      });
    });
  });

  return unions.map((entry, idx) => {
    const props = getSchemaProps(entry.schema);
    const required = getSchemaRequired(entry.schema);
    const constFields: Record<string, string | number | boolean> = {};
    Object.entries(props).forEach(([k, v]) => {
      const constVal = getConstLikeValue(v);
      if (constVal !== undefined) constFields[k] = constVal;
    });

    return {
      id: entry.discriminator?.constValue
        ? String(entry.discriminator.constValue)
        : `op_${idx}`,
      label:
        entry.discriminator?.constValue !== undefined
          ? String(entry.discriminator.constValue)
          : `Opción ${idx + 1}`,
      schema: entry.schema,
      properties: props,
      required,
      constFields,
    } satisfies SchemaVariant;
  });
};

export const inferDiscriminatorKey = (
  variants: SchemaVariant[]
): string | undefined => {
  if (variants.length === 0) return undefined;
  const allKeys = variants.map((v) => Object.keys(v.constFields));
  const common = allKeys.reduce<string[]>((acc, keys) => {
    if (!acc.length) return keys;
    return acc.filter((k) => keys.includes(k));
  }, []);
  return common[0];
};

export const inferVariantIndexFromInputs = (
  inputs: Record<string, unknown>,
  variants: SchemaVariant[]
): number => {
  if (variants.length === 0) return 0;

  const scores = variants.map((variant, idx) => {
    const keys = new Set(Object.keys(variant.properties));
    const matchCount = Object.keys(inputs).filter((k) => keys.has(k)).length;
    const requiredHit = Array.from(variant.required).some((k) => k in inputs);
    return { idx, score: matchCount + (requiredHit ? 10 : 0) };
  });

  scores.sort((a, b) => b.score - a.score);
  return scores[0]?.score ? scores[0].idx : 0;
};
